import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { RouteSkeleton } from "@/components/LazyLoadFallbacks";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import Profile from "./pages/Profile";
import PlatformSettings from "./pages/PlatformSettings";
import WorkspaceMembers from "./pages/WorkspaceMembers";
import WorkspaceDetail from "./pages/WorkspaceDetail";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import HelpCenter from "./pages/HelpCenter";
import NotFound from "./pages/NotFound";
import DealSummaryPublic from "./pages/DealSummaryPublic";
import Waitlist from "./pages/Waitlist";
import DealsV2Home from "./pages/DealsV2Home";
import DealsV2MyDeals from "./pages/DealsV2MyDeals";
import DealsV2Reports from "./pages/DealsV2Reports";
import DealV2Wizard from "./pages/DealV2Wizard";
import DealV2Sensitivity from "./pages/DealV2Sensitivity";
import CreateAccount from "./pages/CreateAccount";
import { AuthenticatedLayout } from "./components/AuthenticatedLayout";
import DealV2Layout from "./pages/DealV2Layout";

const MarketInsights = lazy(() => import("./pages/MarketInsights"));
const AdminWaitlist = lazy(() => import("./pages/AdminWaitlist"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminDealTypes = lazy(() => import("./pages/AdminDealTypes"));
const AdminDealTypeDetail = lazy(() => import("./pages/AdminDealTypeDetail"));
const DealsV2Dashboard = lazy(() => import("./pages/DealsV2Dashboard"));
const DealV2Results = lazy(() => import("./pages/DealV2Results"));
const DealV2Negotiation = lazy(() => import("./pages/DealV2Negotiation"));
const DealV2Export = lazy(() => import("./pages/DealV2Export"));
const AdminAgentHub = lazy(() => import("./pages/AdminAgentHub"));
const AdminAgentDetail = lazy(() => import("./pages/AdminAgentDetail"));
const AdminScanLibrary = lazy(() => import("./pages/AdminScanLibrary"));
const AdminScanTemplateDetail = lazy(() => import("./pages/AdminScanTemplateDetail"));
const AdminMarketData = lazy(() => import("./pages/AdminMarketData"));
const AdminMarketDataDetail = lazy(() => import("./pages/AdminMarketDataDetail"));
const DealsV2Analytics = lazy(() => import("./pages/DealsV2Analytics"));
const AdminResearch = lazy(() => import("./pages/AdminResearch"));
const AdminFeedback = lazy(() => import("./pages/AdminFeedback"));

const queryClient = new QueryClient();

const AuthPage = ({ children }: { children: React.ReactNode }) => (
  <AuthenticatedLayout>{children}</AuthenticatedLayout>
);

const AppRoutes = () => (
  <Suspense fallback={<RouteSkeleton />}>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/waitlist" element={<Waitlist />} />
      <Route path="/create-account" element={<CreateAccount />} />
      <Route path="/admin/waitlist" element={<AuthPage><AdminWaitlist /></AuthPage>} />
      <Route path="/admin/dashboard" element={<AuthPage><AdminDashboard /></AuthPage>} />
      <Route path="/admin/deals" element={<AuthPage><AdminDealTypes /></AuthPage>} />
      <Route path="/admin/deals/:id" element={<AuthPage><AdminDealTypeDetail /></AuthPage>} />
      <Route path="/admin/agents" element={<AuthPage><AdminAgentHub /></AuthPage>} />
      <Route path="/admin/agents/:agentId" element={<AuthPage><AdminAgentDetail /></AuthPage>} />
      <Route path="/admin/scan-library" element={<AuthPage><AdminScanLibrary /></AuthPage>} />
      <Route path="/admin/scan-library/:id" element={<AuthPage><AdminScanTemplateDetail /></AuthPage>} />
      <Route path="/admin/market-data" element={<AuthPage><AdminMarketData /></AuthPage>} />
      <Route path="/admin/market-data/:metro" element={<AuthPage><AdminMarketDataDetail /></AuthPage>} />
      <Route path="/admin/research" element={<AuthPage><AdminResearch /></AuthPage>} />
      <Route path="/admin/feedback" element={<AuthPage><AdminFeedback /></AuthPage>} />
      <Route path="/deals-v2" element={<AuthPage><DealsV2Home /></AuthPage>} />
      <Route path="/deals-v2/dashboard" element={<AuthPage><DealsV2Dashboard /></AuthPage>} />
      <Route path="/deals-v2/my-deals" element={<AuthPage><DealsV2MyDeals /></AuthPage>} />
      <Route path="/deals-v2/reports" element={<AuthPage><DealsV2Reports /></AuthPage>} />
      <Route path="/deals-v2/analytics" element={<AuthPage><DealsV2Analytics /></AuthPage>} />
      <Route path="/deals-v2/new/:dealTypeId" element={<AuthPage><DealV2Wizard /></AuthPage>} />
      <Route path="/deals-v2/:dealId" element={<AuthPage><DealV2Wizard /></AuthPage>} />
      <Route path="/deals-v2/:dealId" element={<AuthPage><DealV2Layout /></AuthPage>}>
        <Route path="results" element={<DealV2Results />} />
        <Route path="detail" element={<DealV2Results />} />
        <Route path="sensitivity" element={<DealV2Sensitivity />} />
        <Route path="negotiation" element={<DealV2Negotiation />} />
      </Route>
      <Route path="/deals-v2/:dealId/export" element={<AuthPage><DealV2Export /></AuthPage>} />
      <Route path="/profile" element={<AuthPage><Profile /></AuthPage>} />
      <Route path="/market-insights" element={<AuthPage><MarketInsights /></AuthPage>} />
      <Route path="/settings" element={<AuthPage><PlatformSettings /></AuthPage>} />
      <Route path="/workspace/members" element={<AuthPage><WorkspaceMembers /></AuthPage>} />
      <Route path="/workspace/:id" element={<AuthPage><WorkspaceDetail /></AuthPage>} />
      {/* V1 routes — redirected to V2 equivalents */}
      <Route path="/dashboard" element={<Navigate to="/deals-v2/dashboard" replace />} />
      <Route path="/my-deals" element={<Navigate to="/deals-v2/my-deals" replace />} />
      <Route path="/reports" element={<Navigate to="/deals-v2/reports" replace />} />
      <Route path="/context" element={<Navigate to="/deals-v2" replace />} />
      <Route path="/quick-screen" element={<Navigate to="/deals-v2" replace />} />
      <Route path="/results" element={<Navigate to="/deals-v2/dashboard" replace />} />
      <Route path="/assumptions" element={<Navigate to="/deals-v2/dashboard" replace />} />
      <Route path="/sensitivity" element={<Navigate to="/deals-v2/dashboard" replace />} />
      <Route path="/negotiation" element={<Navigate to="/deals-v2/dashboard" replace />} />
      <Route path="/export" element={<Navigate to="/deals-v2/dashboard" replace />} />
      <Route path="/settings/deal-v1" element={<Navigate to="/settings" replace />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/help-center" element={<AuthPage><HelpCenter /></AuthPage>} />
      <Route path="/deal-summary/:slug" element={<DealSummaryPublic />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  </Suspense>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
